export const resolveInvoiceFormat = (value) => (value === 'rollup' ? 'rollup' : 'regular');

export const isAdjustmentInvoiceGroup = (group) => {
  const eventType = String(group?.eventType ?? group?.type ?? '')
    .trim()
    .toLowerCase();
  const title = String(group?.title ?? '')
    .trim()
    .toLowerCase();
  return eventType === 'adjustment' || eventType === 'adjustments' || /^adjustments?$/.test(title);
};

export const adjustmentReasonLabel = (row) => {
  const reason = String(row?.adjustmentReason ?? '').trim();
  if (reason) return reason;
  const description = String(row?.description ?? '').trim();
  return description || '—';
};

export const invoicePdfParams = (rollup, { links = false } = {}) => {
  const params = {};
  if (typeof rollup === 'boolean') params.rollup = rollup;
  if (links) params.links = true;
  return Object.keys(params).length > 0 ? params : undefined;
};

const numberOrNull = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const quantityForGroup = (group) => {
  const directQuantity = numberOrNull(group?.totalQty ?? group?.qty);
  if (directQuantity != null) return directQuantity;

  const rows = group?.rows ?? group?.entries ?? [];
  if (Array.isArray(rows) && rows.length > 0) {
    const rowQuantities = rows
      .map((row) => numberOrNull(row?.qty))
      .filter((value) => value != null);
    if (rowQuantities.length > 0) {
      return rowQuantities.reduce((total, value) => total + value, 0);
    }
  }

  return numberOrNull(group?.count);
};

// Invoice detail responses are usually grouped by event type already, but
// Storage and legacy invoices can contain more than one group for the same
// event type. Adjustment rows are already grouped by exact reason on the
// server, so preserve those child rows while retaining one Adjustment parent.
export const rollupInvoiceLineItems = (lineItems = []) => {
  const groups = new Map();

  lineItems.forEach((group, index) => {
    const eventType = group?.eventType ?? group?.type ?? `group-${index}`;
    const groupKey = isAdjustmentInvoiceGroup(group) ? 'Adjustment' : eventType;
    const subtotal = numberOrNull(group?.subtotal ?? group?.total ?? group?.totalFee) ?? 0;
    const quantity = quantityForGroup(group);
    const rows = group?.rows ?? group?.entries ?? [];
    const existing = groups.get(groupKey);

    if (existing) {
      existing.subtotal += subtotal;
      if (quantity != null) {
        existing.totalQty = (existing.totalQty ?? 0) + quantity;
        existing.hasQuantity = true;
      }
      if (isAdjustmentInvoiceGroup(group) && Array.isArray(rows)) {
        existing.rows.push(...rows);
      }
      return;
    }

    groups.set(groupKey, {
      eventType: isAdjustmentInvoiceGroup(group) ? 'Adjustment' : eventType,
      title: group?.title,
      subtotal,
      totalQty: quantity ?? 0,
      hasQuantity: quantity != null,
      ...(isAdjustmentInvoiceGroup(group) ? { rows: Array.isArray(rows) ? [...rows] : [] } : {}),
    });
  });

  return [...groups.values()].filter(
    (group) => group.subtotal !== 0 || (isAdjustmentInvoiceGroup(group) && group.rows?.length > 0),
  );
};
